// Sales replay (plan §6.3, D-036): the replays set up so far, their last run, and the way
// to set up another. Placed under "Set up", after Imports: a replay proves an imported
// catalogue against the source's past sales before going live; it isn't a recurring report.
//
// Each replay is a card that opens its run (review UI2A-6): one primary action on the page
// (New replay), the rest secondary; a finished run shows its verdict beside "Finished".

import type { ReplayScenario } from '@enp/domain/api';
import { Button, Card, cn, EmptyState, focusRing, LoadingSkeleton, PageHeader } from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, FileInput, History, Plus, RotateCcw } from 'lucide-react';
import { meQueryOptions } from '../../../auth/session.js';
import { RunStatusBadge, useCanRunReplays, useStartReplayRun } from '../../../replay/components.js';
import { nextStep, optionsSummary, rangeLength, runFailureShort } from '../../../replay/labels.js';
import { connectionsQuery, isBusy, scenariosQuery } from '../../../replay/queries.js';
import { PracticeBranchNote, VerdictBadge } from '../../../replay/report.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { plainDateLabel, accountToday } from '../../../stock/labels.js';
import { AccountDate } from '../../../shell/account-date.js';

export const Route = createFileRoute('/_app/replay/')({
  component: ReplayPage,
});

function ScenarioCard({
  scenario,
  sourceName,
  today,
  canRun,
}: {
  scenario: ReplayScenario;
  sourceName: string;
  today: string;
  canRun: boolean;
}) {
  const run = scenario.last_run;
  const { start, startingId } = useStartReplayRun();
  const busy = run ? isBusy(run.status) : false;
  const failure = run ? runFailureShort(run) : null;
  const next = scenario.archived_at ? 'none' : nextStep(run);
  const open = !run
    ? null
    : busy
      ? 'Follow its progress'
      : run.status === 'completed'
        ? 'Open the report'
        : 'See what happened';
  return (
    <Card
      role="listitem"
      aria-labelledby={`scenario-${scenario.id}`}
      className={cn(
        'relative flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between',
        run && 'transition-colors hover:border-border-strong hover:bg-surface-hover/40',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <h2 id={`scenario-${scenario.id}`} className="font-display text-lg font-semibold text-fg">
          {scenario.name}
        </h2>
        <p className="text-body text-fg">
          {sourceName} sales of {plainDateLabel(scenario.from, today)} to{' '}
          {plainDateLabel(scenario.to, today)}{' '}
          <span className="text-muted">({rangeLength(scenario.from, scenario.to)})</span>
        </p>
        <p className="text-sm text-muted">
          {optionsSummary(scenario.options)} · set up <AccountDate value={scenario.created_at} />
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-start gap-3 sm:items-end">
        {run ? (
          <span className="flex flex-col items-start gap-1 sm:items-end">
            <span className="flex flex-wrap items-center gap-1.5" data-testid="run-badges">
              <RunStatusBadge run={run} />
              <VerdictBadge run={run} />
            </span>
            {failure && <span className="text-xs font-medium text-fg">{failure}</span>}
            <span className="text-xs text-muted">
              {run.finished_at ? (run.status === 'completed' ? 'Finished' : 'Stopped') : 'Started'}{' '}
              <AccountDate value={run.finished_at ?? run.started_at} withTime />
            </span>
          </span>
        ) : (
          <span className="text-sm text-muted">Not run yet</span>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {canRun && next === 'new_scenario' && (
            // A finished or changed replay isn't run again: fixes change its inputs.
            <Button variant="secondary" size="sm" asChild className="relative z-10">
              <Link to="/replay/new" search={{ copy: scenario.id }}>
                <Plus aria-hidden />
                Start a new replay
              </Link>
            </Button>
          )}
          {canRun && (next === 'run' || next === 'run_again') && (
            <Button
              variant="secondary"
              size="sm"
              className="relative z-10"
              icon={<RotateCcw aria-hidden />}
              loading={startingId === scenario.id}
              onClick={() => start({ id: scenario.id })}
            >
              {run ? 'Run again' : 'Run it'}
            </Button>
          )}
          {run && open && (
            // The card is the link (its whole area opens the run); the words say where to.
            <Link
              to="/replay/runs/$runId"
              params={{ runId: run.id }}
              aria-describedby={`scenario-${scenario.id}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-1 text-sm font-medium text-primary-text',
                "after:absolute after:inset-0 after:rounded-lg after:content-['']",
                focusRing,
              )}
            >
              {open}
              <ArrowRight aria-hidden className="size-4" />
            </Link>
          )}
        </div>
      </div>
    </Card>
  );
}

function ReplayPage() {
  useDocumentTitle('Sales replay');
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canRun = useCanRunReplays();
  const scenarios = useQuery(scenariosQuery());
  const connections = useQuery(connectionsQuery());
  const names = new Map((connections.data?.connections ?? []).map((c) => [c.id, c.display_name]));
  const hasSource = (connections.data?.connections.length ?? 0) > 0;
  const items = scenarios.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Sales replay"
        description="Replays your till’s past sales against the imported catalogue, to prove stock, VAT and usage before you go live."
        actions={
          canRun && items.length > 0 ? (
            <Button asChild>
              <Link to="/replay/new">
                <Plus aria-hidden />
                New replay
              </Link>
            </Button>
          ) : undefined
        }
      />
      <PracticeBranchNote scenario={undefined} />
      {scenarios.isPending ? (
        <LoadingSkeleton variant="table" count={3} label="Loading replays" />
      ) : scenarios.isError ? (
        <ErrorPanel
          error={scenarios.error}
          titleAs="h2"
          what="The replays"
          onRetry={() => void scenarios.refetch()}
        />
      ) : items.length === 0 ? (
        hasSource || connections.isPending ? (
          <EmptyState
            icon={History}
            title="No replays yet"
            description="Pick a source and a range of past days. The replay reads those sales, simulates stock and production in a practice branch, and reports what matched."
            action={
              canRun ? (
                <Button asChild>
                  <Link to="/replay/new">
                    <Plus aria-hidden />
                    Set up a replay
                  </Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <EmptyState
            icon={FileInput}
            title="Connect a source first"
            description="A replay reads past sales from the same source your catalogue was imported from. Connect it under Imports."
            action={
              <Button asChild>
                <Link to="/imports">Go to Imports</Link>
              </Button>
            }
          />
        )
      ) : (
        <div role="list" aria-label="Replays" className="flex flex-col gap-3">
          {items.map((s) => (
            <ScenarioCard
              key={s.id}
              scenario={s}
              sourceName={names.get(s.connection_id) ?? 'Source'}
              today={today}
              canRun={canRun}
            />
          ))}
        </div>
      )}
      {!canRun && items.length > 0 && (
        <p className="mt-4 text-sm text-muted">
          You can read the reports. Starting a replay needs catalogue editing for all branches.
        </p>
      )}
    </>
  );
}
